import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import "./ClassScheduleCalnder.css";
import { useAuth } from "../../AuthContext.js";
import configData from "../../configure";

const Calendar = ({ selectedUser }) => {
  const { axios } = useAuth();
  const classId = selectedUser?.studentInfo?.classId;
  const [classData, setClassData] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  useEffect(() => {
    console.log("Selected Class Details:", classId);

    if (!classId) return;

    const fetchData = async () => {
      try {
        const response = await axios.get(`${configData.GET_CLASS_SCHEDULE_BY_ID}/${classId}`);
        console.log("Fetched Class Data:", response.data);
        setClassData(response.data);
      } catch (error) {
        console.error("Error fetching class data:", error);
        setClassData(null); // Set to null on error to handle gracefully
      }
    };

    fetchData();
  }, [classId, axios]);

  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week");

  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const handlePrev = () => {
    if (viewMode === "week") {
      setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 7)));
    } else {
      setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)));
    }
  };

  const handleNext = () => {
    if (viewMode === "week") {
      setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 7)));
    } else {
      setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)));
    }
  };

  const handleViewChange = (event, newValue) => {
    setViewMode(newValue);
  };

  // Function to get the schedule for a specific day
  const renderDaySchedule = (day) => {
    if (!classData || !classData.classScheduleMap || Object.keys(classData.classScheduleMap).length === 0) {
      return <Typography>No schedule available</Typography>;
    }

    const dayName = weekdays[day.getDay()];
    const daySchedule = classData.classScheduleMap[dayName] || [];

    if (daySchedule.length === 0) {
      return <Typography>No schedule for this day</Typography>;
    }

    return (
      <Box className="day-schedule-container">
        {Array.from({ length: 9 }, (_, i) => (
          <Box key={i} className="hour-row">
            <Typography className="hour-label">{`${8 + i}:00`}</Typography>
            <Box className="schedule-items">
              {daySchedule
                .filter(
                  (schedule) =>
                    schedule && parseInt(schedule.startTime?.split(":")[0], 10) === 8 + i
                )
                .map((schedule, index) => (
                  <Box
                    key={index}
                    className="schedule-item"
                    onClick={() => setSelectedSchedule(schedule)}
                  >
                    <Typography>{schedule.courseName || "Unknown Course"}</Typography>
                  </Box>
                ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderWeekView = () => {
    if (!classData || !classData.classScheduleMap || Object.keys(classData.classScheduleMap).length === 0) {
      return <Typography>No schedule available for this week</Typography>;
    }

    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const days = [];

    // Create a single time column for the entire week view
    const timeColumn = (
      <Box className="time-column">
        {Array.from({ length: 9 }, (_, i) => (
          <Box key={i} className="hour-row">
            <Typography className="hour-label">{`${8 + i}:00`}</Typography>
          </Box>
        ))}
      </Box>
    );

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      const dayName = weekdays[day.getDay()];
      const daySchedule = classData.classScheduleMap[dayName] || [];

      days.push(
        <Box key={i} className="calendar-week-day">
          <Typography variant="h6">{weekdays[day.getDay()]}</Typography>
          <Box className="day-schedule-container">
            {Array.from({ length: 9 }, (_, i) => (
              <Box key={i} className="hour-row">
                <Box className="schedule-items">
                  {daySchedule
                    .filter(
                      (schedule) =>
                        schedule && parseInt(schedule.startTime?.split(":")[0], 10) === 8 + i
                    )
                    .map((schedule, index) => (
                      <Box
                        key={index}
                        className="schedule-item"
                        onClick={() => setSelectedSchedule(schedule)}
                      >
                        <Typography>{schedule.courseName || "Unknown Course"}</Typography>
                      </Box>
                    ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    return (
      <Box className="calendar-week-view">
        {timeColumn}
        <Box className="week-days">{days}</Box>
      </Box>
    );
  };

  const renderDayView = () => {
    const day = currentDate;
    return (
      <Box className="calendar-day-view">
        <Typography variant="h6">{`${weekdays[day.getDay()]}, ${day.getDate()} ${
          monthNames[day.getMonth()]
        } ${day.getFullYear()}`}</Typography>
        {renderDaySchedule(day)}
      </Box>
    );
  };

  return (
    <Box className="calendar-wrapper">
      <Box className="calendar-container">
        <Box className="calendar-header">
          <IconButton onClick={handlePrev}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">
            {`${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
          </Typography>
          <IconButton onClick={handleNext}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Tabs value={viewMode} onChange={handleViewChange}>
          <Tab label="Day" value="day" />
          <Tab label="Week" value="week" />
        </Tabs>
        <Box className="calendar-content">
          {classId ? (
            <>
              {viewMode === "day" && renderDayView()}
              {viewMode === "week" && renderWeekView()}
            </>
          ) : (
            <Typography>Please select a class to view the schedule</Typography>
          )}
        </Box>
      </Box>

      {selectedSchedule && (
        <Box className="schedule-details">
          <Typography variant="h6" style={{ marginBottom: "10px", color: "rgba(82, 114, 233, 1)" }}>
            Class Details
          </Typography>
          <Typography><strong>Course Name:</strong> {selectedSchedule.courseName || "Unknown Course"}</Typography>
          <Typography><strong>Start Time:</strong> {selectedSchedule.startTime || "N/A"}</Typography>
          <Typography><strong>Duration:</strong> {selectedSchedule.durationMins || "N/A"} Min</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Calendar;